import Rating from '@/components/Rating.vue';
import sportaanbiederRepository, { ISportaanbieder, ISportaanbiederCertificaat, ISportaanbiederLocatie, ISportaanbiederPhoto, ISportaanbiederSocialType, ClubBadge } from '@/repositories/sportaanbieder.repository';
import Swiper from 'swiper';
import { createApp } from 'vue/dist/vue.esm-bundler.js';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Navigation, EffectFade } from 'swiper/modules';
import { useUrlSearchParams } from '@/helpers/url.helper';
import reviewRepository, { IReview, IReviewStatistic } from '@/repositories/review.repository';
import { SocialType } from '@/models/socialtype.enum';
import activiteitRepository, { Activiteit } from '@/repositories/activiteit.repository';
import { usePlatformStore } from '@/stores/platform.store';
import { createPinia, setActivePinia } from 'pinia';
import UpcomingActivities from './components/UpcomingActivities.vue';
import CmsDataProvider, { CMS_DATA_ID } from '@/providers/cms.provider';
import { Tooltip } from 'floating-vue';
import { formatDate } from '@/helpers/date.helper';
import ReviewOverview from '@/components/Reviews/ReviewOverview.vue';
import Sidebar from '@/components/Sidebar.vue';
import FloatingVue from 'floating-vue'
import trainerRepository, { ITrainer, ITrainerKenmerk } from '@/repositories/trainer.repository';
import kenmerkRepository, { Kenmerk } from '@/repositories/kenmerk.repository';
import TrainerDetail from '@/components/Trainers/TrainerDetail.vue';
import { getPosterImage } from '@/helpers/media.helper';
import { useSocialAccounts } from '@/composables/socialtype.composable';
import LeadCaptureModal from '@/components/Modals/LeadCaptureModal.vue';
import { Notifications } from '@kyvg/vue3-notification';
import velocity from "velocity-animate";
import { getInitialsFromTrainer } from '@/helpers/intials.helper';

const pinia = createPinia();

setActivePinia(pinia)

const app = createApp({
    setup() {
        const platformStore = usePlatformStore();

        const cmsData = CmsDataProvider.get<{
            activitiesSingular: string,
            activitiesPlural: string,
            clubsSingular: string,
            clubsPlural: string,
            trainerSingular: string,
            trainerPlural: string,
            badgesTitle: string
        }>(CMS_DATA_ID.CLUB_DETAIL_V2);

        const sportaanbieder = ref<ISportaanbieder>();
        const reviewStatistic = ref<IReviewStatistic>();
        const reviews = ref<IReview[]>();
        const photos = ref<ISportaanbiederPhoto[]>();
        const trainers = ref<ITrainer[]>();
        const kenmerken = ref<Kenmerk[]>();

        const locations = ref<ISportaanbiederLocatie[]>();
        const socialTypes = ref<ISportaanbiederSocialType[]>();
        const socials = useSocialAccounts(socialTypes);

        const activities = ref<Activiteit[]>();
        const certificaten = ref<ISportaanbiederCertificaat[]>();
        const faciliteiten = ref<Kenmerk[]>();
        const aanvullendeFaciliteiten = ref<Kenmerk[]>();
        const badges = ref<ClubBadge[]>([]);

        const loading = ref(false);
        const searchParams = useUrlSearchParams();

        const mainLocation = computed(() => {
            return locations.value?.find(x => x.LocatieType === 2);
        });

        const adres = computed(() => {
            return [mainLocation.value?.Straatnaam, mainLocation.value?.Huisnummer]
                .filter(v => v && v.trim() !== "")
                .join(" ");
        });

        const woonplaats = computed(() => {
            return [adres.value, mainLocation.value?.Woonplaats]
                .filter(v => v && v.trim() !== "")
                .join(", ");
        })

        const mainLocationInHeader = computed(() => {
            const found = mainLocation.value;

            if (!found) return "";

            return [found.Naam, adres.value, found.Woonplaats]
                .filter(v => v && v.trim() !== "")
                .join(", ");
        })

        const otherLocations = computed(() => {
            const all = locations.value || [];
            const main = mainLocation.value;
            const others = all.filter(x => x.Id !== main?.Id);
            // Uniek op ID
            const unique = others.filter((loc, index, self) => self.findIndex(l => l.Id === loc.Id) === index);
            return unique;
        });

        const googleMapsLink = computed(() => {
            return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(mainLocation.value?.Adres + ', ' + mainLocation?.value?.Woonplaats);
        })
        const getGoogleMapsLink = (loc: ISportaanbiederLocatie) => {
            return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(loc.Adres + ', ' + loc.Woonplaats);
        };
        const website = computed(() => {
            return socialTypes.value?.find(x => x.SocialType == SocialType.Website)?.Account;
        });

        const certification = computed(() => {
            return certificaten.value?.find(x => x.Kenmerk == 289 || x.Id == 289);
        });

        const hasCertification = computed(() => {
            return !!certification.value;
        });

        const certificationTitle = computed(() => {
            return certification.value?.Titel?.trim() || "We love groen";
        });

        const certificationText = computed(() => {
            return certification.value?.Tekst?.trim()
                || 'Onze golfbanen beheren we op een verantwoorde en duurzame wijze. Sterker, we zijn <a href="https://www.ngf.nl/caddie/duurzaam-beheer/on-course-nederland-geo-tips-tricks-faq" target="_blank">GEO-certified</a> voor ons verantwoord en milieuvriendelijk beheer van al ons groen.';
        });

        const certificationImages = computed(() => {
            const customImages = certification.value?.Afbeeldingen
                ?.map(x => x.AfbeeldingUrl)
                .filter((url): url is string => !!url && url.trim() !== "");

            if (customImages && customImages.length > 0) {
                return customImages.map(url => `${url}?width=300&height=300&mode=crop`);
            }

            return [
                "/Images/golfstart/golfbaan-1.jpg",
                "/Images/golfstart/golfspeler-1.jpg",
                "/Images/golfstart/golfbaan-2.jpg",
            ];
        });

        const swiperInstanceHeader = ref<Swiper | null>(null);
        const swiperInstanceImpression = ref<Swiper | null>(null);
        const swiperInstanceReviewsPreview = ref<Swiper | null>(null);
        const swiperInstanceCertification = ref<Swiper[]>([]);

        const initPhotoSwiper = () => {
            if (swiperInstanceHeader.value) return;

            swiperInstanceHeader.value = new Swiper(".photo-swiper", {
                modules: [Navigation, EffectFade],
                slidesPerView: 1,
                height: 210,
                loop: true,
                navigation: {
                    nextEl: ".button-next",
                    prevEl: ".button-prev",
                },
            });

            swiperInstanceImpression.value = new Swiper(".club-detail__impression-swiper", {
                modules: [Navigation, EffectFade],
                slidesPerView: 1,
                loop: true,
                navigation: {
                    nextEl: ".club-detail__impression-button-next",
                    prevEl: ".club-detail__impression-button-prev",
                },
            });
        };

        const initReviewSwiper = () => {
            if (swiperInstanceReviewsPreview.value) return;

            swiperInstanceReviewsPreview.value = new Swiper(".club-detail__reviews-preview-swiper", {
                modules: [Navigation, EffectFade],
                autoHeight: true,
                slidesPerView: 1,
                loop: true,
                navigation: {
                    nextEl: ".club-detail__reviews-preview-button-next",
                    prevEl: ".club-detail__reviews-preview-button-prev",
                },
            });
        };

        const initCertificationSwiper = () => {
            if (swiperInstanceCertification.value.length > 0) return;

            const certificationSections = document.querySelectorAll<HTMLElement>(".club-detail__certification");

            certificationSections.forEach(section => {
                const swiperElement = section.querySelector<HTMLElement>(".club-detail__certification-images-swiper");
                const nextEl = section.querySelector<HTMLElement>(".club-detail__certification-images-button-next");
                const prevEl = section.querySelector<HTMLElement>(".club-detail__certification-images-button-prev");

                if (!swiperElement || !nextEl || !prevEl) return;

                const instance = new Swiper(swiperElement, {
                    modules: [Navigation, EffectFade],
                    slidesPerView: "auto",
                    spaceBetween: 16,
                    navigation: {
                        nextEl,
                        prevEl,
                    },
                });

                swiperInstanceCertification.value.push(instance);
            });
        };

        const initSportaanbieder = () => {
            const sportaanbiederId = searchParams.getParam("id")?.replace(/\/+$/, '').trim();
            if (!sportaanbiederId) return;

            const filter = {
                type: 'sportaanbod',
                platform_facet: platformStore.platformId,
                facetlist: [],
                pageSize: 10,
                offset: 0,
                OrderBy: 'StartDatum',
                sportaanbieder: sportaanbiederId,
            };

            sportaanbiederRepository.getSportsProviderById(sportaanbiederId)
                .then(data => sportaanbieder.value = data);

            reviewRepository.getReviewStatistics(sportaanbiederId, "sportaanbieder")
                .then(data => reviewStatistic.value = data);

            sportaanbiederRepository.getFotosSportaanbieder(sportaanbiederId)
                .then(data => photos.value = data.filter(x => x.Afbeeldingtype == 1));

            sportaanbiederRepository.getLocatiesBySportaanbieder(sportaanbiederId)
                .then(data => locations.value = data);

            sportaanbiederRepository.getSocialTypesBySportaanbieder(sportaanbiederId)
                .then(data => socialTypes.value = data);

            reviewRepository.getSportaanbiederReviewsMetAntwoorden(sportaanbiederId, 0, false)
                .then(data => reviews.value = data);

            trainerRepository.getPrivelesTrainers(sportaanbiederId)
                .then(data => trainers.value = data);

            kenmerkRepository.getKenmerken()
                .then(data => kenmerken.value = data);

            kenmerkRepository.getKenmerkenByCategorie(4, platformStore.platformId, sportaanbiederId)
                .then(data => faciliteiten.value = data);

            kenmerkRepository.getKenmerkenByCategorie(9, platformStore.platformId, sportaanbiederId)
                .then(data => aanvullendeFaciliteiten.value = data);

            sportaanbiederRepository.getCertificatenBySportaanbieder(sportaanbiederId)
                .then(data => certificaten.value = data);

            activiteitRepository.search(filter)
                .then(result => activities.value = result.Items.map(x => x.Entity));

            sportaanbiederRepository.getBadgesForSportaanbieder(sportaanbiederId)
                .then(results => badges.value = results ?? []);
        };

        const showReviewSidebar = ref(false);

        const openReviewSidebar = () => {
            showReviewSidebar.value = true;
        };

        const getReviewIdFromHash = () => {
            const hash = window.location.hash;
            const match = hash.match(/^#reviews-(.+)$/);

            return match?.[1]?.trim();
        };

        const scrollToReview = (reviewId: string) => {
            const reviewElement = document.getElementById(`reviews-${reviewId}`);

            if (!reviewElement) return false;

            reviewElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

            return true;
        };

        const openReviewSidebarFromHash = async () => {
            const reviewId = getReviewIdFromHash();
            if (!reviewId) return;

            showReviewSidebar.value = true;

            await nextTick();

            if (scrollToReview(reviewId)) return;

            window.setTimeout(() => {
                scrollToReview(reviewId);
            }, 250);
        };

        const handleHashChange = () => {
            void openReviewSidebarFromHash();
        };

        const closeReviewSidebar = () => {
            showReviewSidebar.value = false;
        }

        const showOtherLocationsInHeader = ref(false);

        const toggleOtherLocationsInHeader = () => {
            showOtherLocationsInHeader.value = !showOtherLocationsInHeader.value;
        };

        const showOtherLocationsInContact = ref(false);

        const toggleOtherLocationsInContact = () => {
            showOtherLocationsInContact.value = !showOtherLocationsInContact.value;
        };

        const selectedTrainer = ref<ITrainer>();
        const showTrainerSidebar = ref(false);

        const openTrainerSidebar = (trainer: ITrainer) => {
            selectedTrainer.value = trainer;
            showTrainerSidebar.value = true;
        };

        const closeTrainerSidebar = () => {
            selectedTrainer.value = undefined;
            showTrainerSidebar.value = false;
        }

        const showLeadModal = ref(false);
        const leadModalTargetUrl = ref<string | null>(null);

        const openLeadModal = (url?: string) => {
            leadModalTargetUrl.value = url ?? null;
            showLeadModal.value = true;
        }

        const closeLeadModal = () => {
            showLeadModal.value = false;
            leadModalTargetUrl.value = null;
        }

        const getKenmerkNaam = (kenmerk: ITrainerKenmerk) => {
            return kenmerken.value?.find(x => x.Id == kenmerk.Kenmerk)?.Naam;
        }

        const videoEmbedUrl = computed(() => {
            if (!sportaanbieder.value?.VideoUrl) return "";

            const url = sportaanbieder.value.VideoUrl;
            const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([\w-]+)/);
            const videoId = match ? match[1] : null;

            return videoId ? `https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0` : "";
        });

        watch(photos, async (val) => {
            if (!val?.length) return;

            await nextTick();
            initPhotoSwiper();
        });

        watch(reviews, async (val) => {
            if (!val?.length) return;

            await nextTick();
            initReviewSwiper();

            void openReviewSidebarFromHash();
        });

        watch(certificaten, async (val) => {
            if (!val?.find(x => x.Kenmerk == 289 || x.Id == 289)) return;

            await nextTick();
            initCertificationSwiper();
        });

        onMounted(() => {
            initSportaanbieder();

            window.addEventListener('hashchange', handleHashChange);
            void openReviewSidebarFromHash();
        });

        onBeforeUnmount(() => {
            window.removeEventListener('hashchange', handleHashChange);

            swiperInstanceCertification.value.forEach(instance => instance.destroy(true, true));
            swiperInstanceCertification.value = [];
        });

        return {
            cmsData,
            loading,
            sportaanbieder,
            reviewStatistic,
            reviews,
            photos,
            mainLocation,
            mainLocationInHeader,
            woonplaats,
            googleMapsLink,
            adres,
            website,
            activities,
            trainers,
            kenmerken,
            faciliteiten,
            aanvullendeFaciliteiten,
            certificaten,
            hasCertification,
            certificationTitle,
            certificationText,
            certificationImages,
            socials,
            badges,

            formatDate,

            showReviewSidebar,
            openReviewSidebar,
            closeReviewSidebar,

            otherLocations,
            showOtherLocationsInHeader,
            toggleOtherLocationsInHeader,
            showOtherLocationsInContact,
            toggleOtherLocationsInContact,

            getGoogleMapsLink,

            selectedTrainer,
            showTrainerSidebar,
            openTrainerSidebar,
            closeTrainerSidebar,

            leadModalTargetUrl,
            showLeadModal,
            openLeadModal,
            closeLeadModal,

            getKenmerkNaam,
            videoEmbedUrl,
            getInitialsFromTrainer,
            getPosterImage,

            isGolfPlatform: platformStore.isGolfPlatform
        };
    }
});

app.use(pinia);
app.use(FloatingVue, {
    themes: {
        'badge-info': {
            $extend: 'tooltip',
        },
    },
})
app.use(Notifications, { velocity })

app.component('ClubRating', Rating);
app.component('ClubUpcomingActivities', UpcomingActivities);
app.component('Tooltip', Tooltip);
app.component('Sidebar', Sidebar);
app.component('ReviewOverview', ReviewOverview)
app.component('TrainerDetail', TrainerDetail)
app.component('LeadCaptureModal', LeadCaptureModal)

app.mount('#club-detail-v2');